<!-- to-do disabled user -->
<template>
  <div class="notification-panel">
    <div class="notification-panel-title">Notification Status</div>
    <fts-tabs class="comfort-form-design">
      <fts-tab title="Templates">
        <div class="notification-panel-control">
          <fts-select
            v-model="formData.moduleId"
            :options="modules"
            :clearable="false"
            comfort-design
            class="notification-panel-select"
            @option:selected="loadModuleDesign"
            v-if="enableModuleSelect"
          >
            <fts-label-group
              slot="selected-option-container"
              :item-id="formData.moduleId"
              :list="modules"
              emoji
            />
            <fts-label-group
              slot="option"
              slot-scope="{option}"
              :item="option"
              emoji
            />
          </fts-select>
          <fts-select
            v-model="scheduledEventStatus"
            :options="scheduledEventStatuses"
            :clearable="false"
            comfort-design
            class="notification-panel-select notification-panel-select--status"
          />
          <input
            type="text"
            placeholder="Search entry"
            class="fts-form-control notification-panel-input"
            v-model="scheduledEventKeyword"
            @input="debounceNotificationInput"
          />
          <fts-btn text icon :disabled="loading" @click="loadNotificationDashboard">
            <font-awesome-icon :icon="['fas', 'rotate']" />
          </fts-btn>
        </div>

        <div class="notification-panel-content">
          <template v-for="(x, ind) in scheduledEvents">
            <div :key="x._id" class="np-card">
              <div class="np-card__l">
                <div class="np-card-entry" v-if="x.entry">
                  <a
                    :href="x.entryURL"
                    target="_blank"
                    class="fts-link-primary"
                    v-if="x.entryURL"
                  >
                    {{x.entry}}
                  </a>
                  <template v-else>
                    {{x.entry}}
                  </template>
                </div>
                <div class="np-card-title">
                  <span class="np-card-title-label">
                    {{getNotificationInformationDescription(x.templateId)}} 
                  </span>
                  <div v-if="isBeta"><a @click="run(x)" class="fts-link-primary">Run Manually</a></div>
                </div>
              </div>
              <div class="np-card__r">
                <div class="np-card-state" :class="automationStatusClass(x.status)">
                  {{automationStatusText(x.status)}}
                </div>
                <div class="np-card-remark" v-if="x.remarks">
                  {{x.remarks}}
                </div>

                <!-- SS04944: the drain records the whole exception on the event, but the panel
                     showed only "Aborted" - so the cause of a failed notification was only ever
                     readable in the server log. -->
                <div class="np-card-error" v-if="x.error">
                  <div class="np-card-error-summary">{{errorSummary(x.error)}}</div>
                  <a
                    class="fts-link-primary np-card-error-toggle"
                    v-if="hasErrorDetail(x.error)"
                    @click="toggleError(x)"
                  >
                    {{expandedErrors[x._id] ? 'Hide details' : 'Show details'}}
                  </a>
                  <pre class="np-card-error-detail" v-if="expandedErrors[x._id]">{{x.error}}</pre>
                </div>

                <div class="np-card-date" v-if="x.targetTime || x.targetDate">
                  <span v-if="x.recurring">(Recurring)</span> {{ x.targetTime ? moment(x.targetTime).format('DD MMM YYYY h:mm A') : moment(x.targetDate).format('DD MMM YYYY')}}
                </div>
              </div>
            </div>

            <div :key="`divider-${ind}`" class="np-card-divider" v-if="ind < scheduledEvents.length - 1"></div>
          </template>

          <div class="notification-panel-loader" v-if="loading">
            <fts-spinner />
          </div>

          <div
            class="fts-not-found notification-panel-not-found"
            v-else-if="!(scheduledEvents && scheduledEvents.length)"
          >
            <div class="not-found-icon">
              <font-awesome-icon :icon="['fas', 'magnifying-glass']" />
            </div>

            <div class="not-found-title">
              No results found
            </div>
          </div>
        </div>
      </fts-tab>

      <fts-tab title="Notification Delivery">
        <div class="notification-panel-control fts-form-group">
          <fts-select
            v-model="formData.moduleId"
            :options="modules"
            :clearable="false"
            comfort-design
            class="notification-panel-select"
            @option:selected="loadModuleDesign"
            v-if="enableModuleSelect"
          >
            <fts-label-group
              slot="selected-option-container"
              :item-id="formData.moduleId"
              :list="modules"
              emoji
            />
            <fts-label-group
              slot="option"
              slot-scope="{option}"
              :item="option"
              emoji
            />
          </fts-select>
          <input
            type="text"
            placeholder="Search entry, email, targets, status, remarks"
            class="fts-form-control notification-panel-input"
            v-model="notificationDeliveryKeyword"
            @input="debounceNotificationInput"
          />
          <fts-btn text icon :disabled="loading" @click="loadNotificationDashboard">
            <font-awesome-icon :icon="['fas', 'rotate']" />
          </fts-btn>
        </div>

        <div class="notification-panel-content">
          <template v-for="(x, ind) in notificationDeliveries">
            <div :key="x.id" class="np-card">
              <div class="np-card__l">
                <div class="np-card-entry" v-if="x.entry">
                  <a
                    :href="x.entryURL"
                    target="_blank"
                    class="fts-link-primary"
                    v-if="x.entryURL"
                  >
                    {{x.entry}}
                  </a>
                  <template v-else>
                    {{x.entry}}
                  </template>
                </div>

                <div class="np-card-title">
                  <span class="np-card-title-icon" v-if="channelIcon(x.channel)">
                    <font-awesome-icon :icon="channelIcon(x.channel)" />
                  </span>
                  <span class="np-card-title-label">
                    {{getNotificationInformationDescription(x.source, x.channel)}}
                  </span>
                </div>
              </div>
              <div class="np-card__content">
                <div class="np-card-target-outer">
                  <div class="np-card-target-container">
                    <div
                      v-for="(target, targetIndex) in x.targets"
                      :key="`target-${targetIndex}`"
                      class="np-card-target"
                    >
                      <template v-if="x.channel === 0 || x.channel === 1">
                        <fts-profile-badge
                          :users="users"
                          :username="target"
                          fallback-name="(disabled user)"
                          v-if="isEmail(target)"
                        />
                        <fts-profile-badge
                          :users="users"
                          :profile-id="target"
                          fallback-name="(disabled user)"
                          v-else
                        />
                      </template>
                      <template v-else>
                        {{target}}
                      </template>
                    </div>
                  </div>
                </div>

                <div class="np-card-subject" v-if="Boolean(x.subject)">
                  {{x.subject}}
                </div>
              </div>
              <div class="np-card__r">
                <div class="np-card-state" :class="statusClass(x.status)">
                  {{statusText(x.status)}}
                </div>

                <div class="np-card-remark" v-if="x.remarks">
                  {{x.remarks}}
                </div>

                <div class="np-card-date"  v-if="x.deliveredOn">
                  {{moment(x.deliveredOn).format('DD MMM YYYY h:mm A')}}
                </div>
              </div>
            </div>

            <div :key="`divider-${ind}`" class="np-card-divider" v-if="ind < notificationDeliveries.length - 1"></div>
          </template>

          <div class="notification-panel-loader" v-if="loading">
            <fts-spinner />
          </div>

          <div
            class="fts-not-found notification-panel-not-found"
            v-else-if="!(notificationDeliveries && notificationDeliveries.length)"
          >
            <div class="not-found-icon">
              <font-awesome-icon :icon="['fas', 'magnifying-glass']" />
            </div>

            <div class="not-found-title">
              No results found
            </div>
          </div>
        </div>
      </fts-tab>
    </fts-tabs>
  </div>
</template>

<script>
import moment from 'moment';

import { authorizationStore } from '../store/authorizationStore';
import { store as canvasDesignStore } from '../store/canvasDesignStore'; 

import commons from '../commons/commons';
import { emailValidator } from '../commons/validator';
import notification from '../commons/notification';

import FTSBtn from '../elements/FTSBtn';
import FTSLabelGroup from '../elements/FTSLabelGroup';
import FTSProfileBadge from '../elements/FTSProfileBadge';
import FTSSelect from '../elements/FTSSelect';
import FTSSpinner from '../elements/FTSSpinner';
import FTSTabs from '../elements/tabs/FTSTabs';
import FTSTab from '../elements/tabs/FTSTab';

export default {
  props: {
    enableModuleSelect: {
      type: Boolean,
      required: false
    }
  },
  components: {
    "fts-btn": FTSBtn,
    "fts-label-group": FTSLabelGroup,
    "fts-profile-badge": FTSProfileBadge,
    "fts-select": FTSSelect,
    "fts-spinner": FTSSpinner,
    "fts-tabs": FTSTabs,
    "fts-tab": FTSTab
  },
  data: () => ({
    scheduledEventKeyword: null,
    scheduledEventStatus: "",
    scheduledEventStatuses: [
      { id: "", name: "Any status" },
      { id: "2", name: "Aborted" },
      { id: "0", name: "Pending" },
      { id: "6", name: "Processing" },
      { id: "1", name: "Executed" },
      { id: "3", name: "Condition Not Met" },
      { id: "4", name: "No Recipient" },
      { id: "5", name: "Trigger Condition invalid" },
      { id: "7", name: "Expired" }
    ],
    expandedErrors: {},
    notificationDeliveryKeyword: null,
    scheduledEvents: [],
    notificationDeliveries: [],
    loading: false,
    formData: {},
    selectedModuleDesign: {}
  }),
  methods: {
    moment,
    async run(scheduledEvent) {
      // the card label can be blank when the trigger is gone from the design — keep the prompt readable
      const description = this.getNotificationInformationDescription(scheduledEvent.templateId) || "This notification";
      const target = scheduledEvent.entry ? `${description} (${scheduledEvent.entry})` : description;
      const res = await this.$confirmation.show({
        title: "Run this notification now?",
        text: `${target} will run immediately, ahead of its schedule. This cannot be undone.`
      });
      if (res !== true) return;

      await canvasDesignStore.dispatch('runScheduledEvent', scheduledEvent);
      await this.loadNotificationDashboard();
    },
    channelIcon(channel) {
      return notification.channelIcon(channel);
    },
    channelText(channel) {
      return notification.channelText(channel);
    },
    statusText(status) {
      return notification.statusText(status);
    },
    statusClass(status) {
      switch (status) {
        case 1:
          return "np-card-state--success";
        case 2:
        case 3:
          return "np-card-state--danger";
      }
    },
    // SS04944: the recorded error is a full exception dump. Its first line carries the type and
    // message - what a reader needs - so the card shows that and keeps the stack one click away.
    errorSummary(error) {
      const firstLine = String(error || "")
        .split("\n")
        .map(x => x.trim())
        .find(x => x.length);

      return firstLine || "";
    },
    hasErrorDetail(error) {
      return String(error || "").trim() !== this.errorSummary(error);
    },
    toggleError(scheduledEvent) {
      const key = scheduledEvent._id;
      this.$set(this.expandedErrors, key, !this.expandedErrors[key]);
    },
    automationStatusText(status) {
      switch (status) {
        case 0:
          return "Pending";
        case 1:
          return "Executed";
        case 2:
          return "Aborted";
        case 3:
          return "Condition Not Met";
        case 4:
          return "No Recipient";
        case 5:
          return "Trigger Condition invalid";
        // SS05943: status 6 is ScheduledEventStatus.Processing - it had no label at all, so the
        // dashboard showed a blank state for an automation that is mid-run.
        case 6:
          return "Processing";
        case 7:
          return "Expired";
          
      }
    },
    automationStatusClass(status) {
      switch (status) {
        case 1:
          return "np-card-state--success";
        case 2:
          return "np-card-state--danger";
        case 3:
          return "np-card-state--ternary-blue";
        case 4:
          return "np-card-state--warning";
        case 5:
          return "np-card-state--teal";
        case 7:
          return "np-card-state--teal";
      }
    },
    resetSearch() {
      this.scheduledEventKeyword = null;
      this.notificationDeliveryKeyword = null;
      this.scheduledEvents = [];
      this.notificationDeliveries = [];
    },
    async loadNotificationDashboard() {
      const moduleId = (this.formData && this.formData.moduleId) || this.moduleId;
      let vm = this;
      try {
        vm.loading = true;
        let result = await canvasDesignStore.dispatch('loadNotifcationDashboard', {
          moduleId: moduleId,
          searchTemplate: this.scheduledEventKeyword,
          searchStatus: this.scheduledEventStatus,
          searchDelivery: this.notificationDeliveryKeyword
        });
        vm.scheduledEvents = result.data.scheduledEvents;
        vm.notificationDeliveries = result.data.notificationDeliveries;
        if (vm.scheduledEvents && vm.scheduledEvents.length) {
          vm.scheduledEvents.forEach(x => {
            if (x.locusId) {
              x.entryURL = `/#/dashboard?id=${commons.queryEncodeids(moduleId, x.locusId)}`;
            }
          });
        }
        if (vm.notificationDeliveries && vm.notificationDeliveries.length) {
          vm.notificationDeliveries.forEach(x => {
            if (x.locusId) {
              x.entryURL = `/#/dashboard?id=${commons.queryEncodeids(moduleId, x.locusId)}`;
            }
          });
        }
      } catch(err) {
        console.error(err);
      } finally {
        vm.loading = false;
      }
    },
    // async preview(scheduleEvent) {
    //   let response = await canvasDesignStore.dispatch("previewAutomation", {
    //     id: scheduleEvent.id,
    //   });
    // },
    // async test(scheduledEvent) {},
    // getNotificationInformation(templateId) {
    //   var trigger = canvasDesignStore.state.vector.extension.triggers.find(
    //     (x) => x.id == templateId
    //   );
    //   return trigger;
    // },
    getNotificationInformationDescription(templateId, channel) {
      const moduleExtension = this.enableModuleSelect ? this.selectedModuleDesign.extension : canvasDesignStore.state.vector.extension;
      var trigger = moduleExtension.triggers && moduleExtension.triggers.find(
        (x) => x.id == templateId
      );

      if (trigger && trigger.description) {
        return trigger.description;
      } else if (trigger) {
        var allElements = [];
        if (moduleExtension.information) {
          allElements = allElements.concat(moduleExtension.information);
        }

        if (moduleExtension.activities) {
          allElements = allElements.concat(moduleExtension.activities);
        }

        if (moduleExtension.states) {
          allElements = allElements.concat(moduleExtension.states);
        }

        if (moduleExtension.standardActivities) {
          allElements = allElements.concat(moduleExtension.standardActivities);
        }

        if(!trigger.item) {
          return `Module: ${trigger.description} ${trigger.id}`;
        }

        var element = allElements.find((x) => x.id == trigger.item);
        if (element) {
          return `${trigger.notification ? "Notification" : "Script"} on ${
            element.name
          }`;
        }
      }

      return this.channelText(channel);
    },
    debounceNotificationInput: _.debounce(function() {
      this.loadNotificationDashboard();
    }, 800),
    isEmail(value) {
      return emailValidator(value && value.trim());
    },
    async loadModuleDesign() {
      try {
        this.loading = true;
        const res = await canvasDesignStore.dispatch("loadModuleDesign", this.formData.moduleId);
        this.selectedModuleDesign = res.data;
        this.resetSearch();
        await this.loadNotificationDashboard();
      } catch(err) {
        this.$toasted.global.error({
          response: err.response,
          message: 'Failed to load module'
        });
      } finally {
        this.loading = false;
      }
    }
  },
  computed: {
    users() {
      return canvasDesignStore.getters.workspaceUsers;
    },
    moduleId() {
      return canvasDesignStore.state.vector && canvasDesignStore.state.vector.id;
    },
    modules() {
      return canvasDesignStore.getters.workspaceModules;
    },
    isBeta() {
      return (
        authorizationStore.getters.isBeta || 
        (this.$route.name === 'builder' || this.$route.name === 'studio' ? canvasDesignStore.getters.workspaceBeta : clientStore.getters.workspaceBeta)
      );
    }
  },
  watch: {
    moduleId() {
      if (!this.enableModuleSelect) {
        this.resetSearch();
        this.loadNotificationDashboard();
      }
    },
    scheduledEventStatus() {
      this.loadNotificationDashboard();
    }
  },
  mounted() {
    !this.enableModuleSelect && this.loadNotificationDashboard();
  }
};
</script>

<style lang="scss" scoped>
@use "./src/assets/scss/vars" as *;

.notification-panel {
  min-height: 20rem;
  .notification-panel-title {
    margin-bottom: 0.5rem;
    font-weight: 500;
    font-size: 1.125rem;
    line-height: 1.5;
  }
  .notification-panel-content {
    position: relative;
  }
  .notification-panel-loader {
    position: absolute;
    inset: 0;
    background-color: transparent;
    cursor: wait;
    > * {
      padding-top: 3rem;
    }
  }
  .notification-panel-not-found {
    padding-top: 3rem;
  }
  .notification-panel-control {
    display: flex;
    align-items: center;
    margin-bottom: 1rem;
    .notification-panel-input {
      margin-right: 1rem;
    }
    .notification-panel-select {
      margin-right: 0.5rem;
    }
  }
  .notification-panel-input {
    max-width: 350px;
  }
  .notification-panel-select {
    width: 250px;
    max-width: 250px;
  }
  .notification-panel-select--status {
    width: 200px;
    max-width: 200px;
  }
  .np-card {
    display: flex;
    align-items: flex-start;
    padding: 0.25rem 0.375rem;
    border-radius: 0.375rem;
    font-size: 0.875rem;
    line-height: 1.5;
    color: var(--fts-text);
    &:hover {
      background-color: rgba(var(--fts-gray-100-rgb), 0.75);
    }
    .np-card__l {
      flex: 1 0 250px;
      max-width: 250px;
      margin-right: 1.5rem;
    }
    .np-card__content {
      min-width: 0;
      flex: 1;
      margin-right: 1.5rem;
      & + .np-card__r {
        flex: 1 0 400px;
        max-width: 400px;
      }
    }
    .np-card__r {
      flex: 1;
      align-items: flex-end;
      display: flex;
      flex-direction: column;
      > *:not(:first-child) {
        margin-top: 0.25rem;
      }
    }
    .np-card-title {
      margin-top: 0.25rem;
      word-break: break-all;
      .np-card-title-icon {
        & + .np-card-title-label {
          margin-left: 0.125rem;
        }
      }
    }
    .np-card-subject {
      overflow-wrap: break-word;
    }
    .np-card-target-outer {
      margin-bottom: 0.25rem;
      .np-card-target-container {
        @include flex-gap($display: flex, $width: auto, $gap: 0.5rem);
        min-width: 0;
        .np-card-target {
          min-width: 0;
          padding: 0.125rem 0.375rem;
          border-radius: 0.5rem;
          border: 1px solid var(--fts-border);
          background-color: var(--fts-card-background);
        }
      }
    }
    .np-card-state {
      display: inline-flex;
      padding-left: 0.25rem;
      padding-right: 0.25rem;
      border-radius: 0.25rem;
      background-color: rgba(var(--fts-text-rgb), 0.125);
      color: var(--fts-text);
      cursor: default;
      &.np-card-state--success {
        background-color: rgba(var(--fts-success-rgb), 0.125);
        color: var(--fts-success);
      }
      &.np-card-state--danger {
        background-color: rgba(var(--fts-danger-rgb), 0.125);
        color: var(--fts-danger);
      }
      &.np-card-state--warning {
        background-color: rgba(var(--fts-warning-rgb), 0.125);
        color: var(--fts-warning);
      }
      &.np-card-state--ternary-blue {
        background-color: rgba(var(--fts-ternary-rgb), 0.125);
        color: var(--fts-ternary-blue);
      }
      &.np-card-state--teal {
        background-color: rgba(#0d9488, 0.125);
        color: #0d9488;
      }
    }
    .np-card-remark {
      text-align: right;
      white-space: pre-wrap;
    }
    .np-card-error {
      align-self: stretch;
      text-align: left;
      color: var(--fts-danger);
    }
    .np-card-error-summary {
      white-space: pre-wrap;
      overflow-wrap: anywhere;
    }
    .np-card-error-toggle {
      cursor: pointer;
    }
    .np-card-error-detail {
      margin: 0.25rem 0 0;
      padding: 0.375rem 0.5rem;
      max-height: 16rem;
      overflow: auto;
      border-radius: 0.25rem;
      background-color: rgba(var(--fts-danger-rgb), 0.08);
      color: var(--fts-text);
      font-family: inherit;
      font-size: 0.8125rem;
      white-space: pre-wrap;
      overflow-wrap: anywhere;
    }
    .np-card-date {
      color: var(--fts-default);
    }
  }
  .np-card-divider {
    margin-top: 0.25rem;
    margin-bottom: 0.25rem;
    border-top: 1px solid rgba(var(--fts-gray-100-rgb), 0.75);
  }
}
</style>